import type {
  FormValues,
  MutationError,
  MutationPayload,
  QueryContextKey,
  QueryData
} from './types'
import type { Group } from '../../API/group'
import { GroupApi } from '../../API/group'
import { PushType, mapper as pushTypeMapper } from '../../domain/push-type.enum'
import type { ListQuerySortField, ListQuerySortOrder, ListState, Location } from '../premium/types'
import type { TableColumnsType } from 'antd'
import { Button, Dropdown, Form, Input, Space, Table, Tooltip } from 'antd'

import {
  CaretDownOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteFilled,
  SearchOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounce, useInterval } from 'react-use'
import { UserApi } from '../../API/user'
import { CellTitle } from '../premium/components/cell-title'
import { EmptyCellValue } from '../premium/components/empty-cell-value'
import { ExpandIcon } from '../premium/components/expand-icon'
import { duration } from '../premium/formatters'

import { ExpandedRow } from './components/expanded-row'
import { baseCellClassName, baseHeaderCellClassName, flags } from './constants'
import { useStore } from './store'

const initialValues: FormValues = {
  name: '',
  id: '',
  funding_type: null,
  network: 'none',
  location: {
    short_name: '',
    id: '',
    name: '',
    location: '',
    key: '',
    created_at: '',
    updated_at: ''
  },
  group_account_number: null,
  group_routing_number: null,
  group_originator_id: null,
  group_banking_id: null,

  claims_account_number: null,
  claims_routing_number: null,
  claims_banking_id: null,
  claims_originator_id: null,

  network_account_number: null,
  network_routing_number: null,
  network_banking_id: null,
  network_originator_id: null,

  checks_only: false,
  full_amount: false,
  level_funded: false,
  zelis_eligible: false,
  activation_date: null,
  status: false,

  reports: []
}

export function NewGroupsPage(): JSX.Element {
  const [form] = Form.useForm<FormValues>()
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form])

  const [state, dispatch] = useStore()

  const [debouncedQuery, setDebouncedQuery] = useState(state.searchParams.query)

  useDebounce(
    () => {
      const query = (state.searchParams.query ?? '').trim().toLowerCase()

      setDebouncedQuery(query)
      dispatch({ type: 'set-pagination', payload: { ...state.pagination, currentPage: 0 } })
    },
    500,
    [state.searchParams.query]
  )

  const query = useQuery<unknown, unknown, QueryData, QueryContextKey>({
    queryKey: [
      'new-groups',
      state.sort,
      state.pagination,
      { ...state.searchParams, query: debouncedQuery }
    ],

    refetchOnMount: true,

    async queryFn(context) {
      const [, sort, pagination, searchParams] = context.queryKey
      const { pageSize, currentPage } = pagination
      const { query = '', locationIds, nextPullDate, flags, pushTypes, networks } = searchParams

      const searchFields = ['name', 'number']

      const data = await GroupApi.getGroups({
        'page-size': pageSize.toString(10),
        ...(currentPage > 0 ? { page: currentPage.toString(10) } : {}),
        ...(typeof sort.field === 'string' ? { 'order-by': sort.field } : {}),
        ...(typeof sort.order === 'string' ? { direction: sort.order } : {}),
        ...(query.length > 0 ? { query } : {}),
        'search-fields': searchFields,
        ...(locationIds.length > 0 ? { locations: locationIds } : {}),
        'next-pull-date': nextPullDate,
        ...(flags.length > 0 ? { flag: flags } : {}),
        ...(pushTypes.length > 0 ? { 'push-type': pushTypes } : {}),
        ...(networks.length > 0 ? { network: networks } : {})
      })

      return {
        rows: data.rows,
        totalFiltered: data.total_filtered,
        totalUnfiltered: data.total_unfiltered,
        filters: data.filters
      }
    },

    onSuccess(_data) {
      dispatch({ type: 'clear-expanded-row-id' })
    },

    keepPreviousData: true
  })

  const mutation = useMutation<unknown, MutationError, MutationPayload>({
    async mutationFn({ id, ...data }: MutationPayload) {
      await GroupApi.updateNetworkGroup(id, data)

      dispatch({ type: 'clear-expanded-row-id' })
      void query.refetch()
    }
  })

  const locationsQuery = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],

    refetchOnMount: true,

    async queryFn() {
      return UserApi.getLocations()
    }
  })

  const columns = useMemo<TableColumnsType<Group>>(
    () => [
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='number' sortColumns={sortColumns}>
              Group #
            </CellTitle>
          )
        },
        key: 'number',
        dataIndex: 'number',
        sorter: true,
        showSorterTooltip: false,
        width: '12%',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title({ sortColumns }) {
          return (
            <CellTitle id='name' sortColumns={sortColumns}>
              Name
            </CellTitle>
          )
        },
        key: 'name',
        dataIndex: 'name',
        sorter: true,
        showSorterTooltip: false,
        width: '51%',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Location',
        key: 'location',
        dataIndex: ['location', 'short_name'],
        width: '7%',
        align: 'center',

        filters: locationsQuery.data
          ?.map(location => {
            return {
              value: location.id,
              name: location.name,
              short_name: location.short_name
            }
          })
          .map(filter => ({
            text: (
              <div
                className={cx(
                  'flex justify-between items-center gap-6',
                  filter.value === null && 'text-gray-400'
                )}
              >
                <span>
                  {filter.name} <span className='text-gray-500'>({filter.short_name})</span>
                </span>
              </div>
            ),
            value: filter.value
          })),

        filterMultiple: true,
        filteredValue: state.searchParams.locationIds,

        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Push Type',
        key: 'push_type',
        dataIndex: 'push_type',
        width: '7%',
        align: 'center',

        filters: [
          { text: pushTypeMapper[PushType.Push], value: PushType.Push },
          { text: pushTypeMapper[PushType.Pull], value: PushType.Pull },
          { text: pushTypeMapper[PushType.FBB], value: PushType.FBB }
        ],

        filterMultiple: true,
        filteredValue: state.searchParams.pushTypes,

        render(value: PushType) {
          return pushTypeMapper[value] || <EmptyCellValue />
        },

        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Network',
        key: 'network',
        dataIndex: 'network',
        width: '7%',
        align: 'center',

        filters: [
          { text: 'None', value: 'null' },
          { text: 'Nomi', value: 'nomi' },
          { text: 'Quantify', value: 'quantify' }
        ],

        filterMultiple: true,
        filteredValue: state.searchParams.networks,

        render(value: string) {
          if (!value || value === 'none') {
            return <EmptyCellValue />
          }

          return value.charAt(0).toUpperCase() + value.slice(1)
        },

        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Flags',
        key: 'flags',
        dataIndex: 'flags',
        align: 'center',

        filters: flags.map(flag => {
          const count = query.data?.filters?.flags?.find(
            filter => filter.value === flag.value
          )?.count

          return {
            value: flag.value,
            text: (
              <div className={cx('flex gap-6 justify-between items-center')}>
                <span>{flag.text}</span>

                {typeof count === 'number' && <span className='text-gray-400'>{count}</span>}
              </div>
            )
          }
        }),
        filterMultiple: true,
        filteredValue: state.searchParams.flags,

        render(value: string[], _record, _index) {
          if (!value?.length) {
            return <EmptyCellValue />
          }

          return (
            <Space>
              {value.map(v => {
                const flag = flags.find(flag => flag.value === v)

                if (!flag) {
                  return null
                }

                return (
                  <Tooltip title={flag.text} key={flag.value}>
                    <flag.icon className={flag.className} />
                  </Tooltip>
                )
              })}
            </Space>
          )
        },
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      }
    ],
    [locationsQuery.data, state.searchParams, query.data?.filters]
  )

  const handleSave = useCallback(() => {
    const { id } = state.expandedRow
    const values = form.getFieldsValue()

    if (id === undefined) {
      return
    }

    const reports = form.getFieldValue('reports')

    mutation.mutate({
      id,
      network: values.network ?? 'none',

      network_account_number: values.network_account_number ?? null,
      network_routing_number: values.network_routing_number ?? null,
      network_banking_id: values.network_banking_id ?? null,
      network_originator_id: values.network_originator_id ?? null,
      reports: reports ?? null
    })
  }, [form, mutation, state.expandedRow])

  const handleCancel = useCallback(() => {
    form.setFieldsValue(initialValues)
    dispatch({ type: 'clear-expanded-row-id' })
  }, [form, dispatch])

  function handleRowExpand(expanded: boolean, record: Group): void {
    if (!expanded) {
      form.setFieldsValue(initialValues)
      dispatch({ type: 'clear-expanded-row-id' })
      return
    }

    form.setFieldsValue({
      name: record.name,
      id: record.id,
      location: record.location,
      funding_type: record.funding_type ?? undefined,
      network: record.network ?? 'none',
      group_account_number: record.group_account_number,
      group_routing_number: record.group_routing_number,
      group_banking_id: record.group_banking_id,
      group_originator_id: record.group_originator_id,

      claims_account_number: record.claims_account_number,
      claims_banking_id: record.claims_banking_id,
      claims_originator_id: record.claims_originator_id,
      claims_routing_number: record.claims_routing_number,

      network_account_number: record.network_account_number,
      network_routing_number: record.network_routing_number,
      network_originator_id: record.network_originator_id,
      network_banking_id: record.network_banking_id,

      checks_only: record.checks_only ?? false,
      full_amount: record.full_amount ?? false,
      level_funded: record.level_funded ?? false,
      zelis_eligible: record.zelis_eligible ?? false,
      activation_date: record.activation_date ? dayjs(record.activation_date) : null,
      status: record.status ?? false,
      reports: record.reports ?? []
    })

    dispatch({ type: 'set-expanded-row-id', payload: record.id })
  }

  const isDefaultFilters = useMemo(() => {
    return !Object.values(state.searchParams).some(value => value.length > 0)
  }, [state.searchParams])

  useInterval(() => {
    void query.refetch()
  }, state.refreshInterval ?? null)

  const onSelectChange = (newSelectedRowKeys: React.Key[], _newSelectedRows: Group[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  }

  return (
    <>
      <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
        <div className='flex justify-between items-start'>
          <div className='flex gap-6 items-center'>
            <h1 className='text-2xl'>Groups</h1>
          </div>
        </div>

        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-col'>
            <div className='flex flex-row gap-2 items-center p-4'>
              <div className='flex-grow'>
                <Input
                  placeholder='Search groups'
                  className='font-lato text-sm py-1 bg-gray-50 [&_input]:bg-transparent rounded [&_input]:placeholder:text-gray-400'
                  prefix={<SearchOutlined className='text-gray-500' />}
                  suffix={
                    (state.searchParams.query.length ?? 0) > 0 ? (
                      <button
                        type='button'
                        className='flex justify-center items-center p-2 -m-2 border'
                        onClick={() => {
                          dispatch({
                            type: 'set-query',
                            payload: ''
                          })
                        }}
                      >
                        <CloseOutlined size={16} className='text-gray-500' />
                      </button>
                    ) : undefined
                  }
                  value={state.searchParams.query ?? ''}
                  onChange={event => {
                    dispatch({
                      type: 'set-query',
                      payload: event.target.value
                    })
                  }}
                />
              </div>

              <Space>
                {!isDefaultFilters && (
                  <Button
                    type='default'
                    onClick={() => {
                      dispatch({
                        type: 'clear-filters'
                      })
                    }}
                  >
                    <span className='mr-2 -translate-y-[4px]'>
                      <DeleteFilled className='text-lucent-primary' />
                    </span>
                    <span className='font-lato font-bold text-lucent-primary text-[10px] uppercase -translate-y-[1px]'>
                      Clear Filters
                    </span>
                  </Button>
                )}

                <Dropdown.Button
                  type='default'
                  disabled={query.isFetching}
                  onClick={() => {
                    void query.refetch()
                  }}
                  icon={
                    <CaretDownOutlined
                      className={cx(
                        'w-[12px] h-[12px] !translate-y-[2px]',
                        typeof state.refreshInterval === 'number' && 'text-lucent-primary',
                        typeof state.refreshInterval !== 'number' && 'text-lucent-gray'
                      )}
                    />
                  }
                  menu={{
                    items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                      key: `refresh-${seconds}`,
                      itemIcon(_props) {
                        if (state.refreshInterval === seconds * 1000) {
                          return <CheckOutlined className='ml-2 text-lucent-primary' />
                        }

                        return null
                      },
                      onClick() {
                        if (state.refreshInterval === seconds * 1000) {
                          dispatch({
                            type: 'clear-refresh-interval'
                          })

                          return
                        }

                        dispatch({
                          type: 'set-refresh-interval',
                          payload: seconds * 1000
                        })
                      },
                      label: duration(seconds * 1000)
                    }))
                  }}
                >
                  <div className='flex items-center space-x-1.5'>
                    <SyncOutlined
                      className={cx(
                        'text-base',
                        query.isFetching && 'animate-custom-spin text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    />

                    <span
                      className={cx(
                        'font-lato font-bold text-[10px] uppercase',
                        query.isFetching && 'text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    >
                      <span>Refresh</span>

                      {typeof state.refreshInterval === 'number' && (
                        <span className=''>
                          <span className='mx-1.5'>|</span>
                          Auto {duration(state.refreshInterval, { short: true })}
                        </span>
                      )}
                    </span>
                  </div>
                </Dropdown.Button>
              </Space>
            </div>
          </div>

          <Table
            size='small'
            columns={columns}
            rowKey='id'
            dataSource={query.data?.rows ?? []}
            loading={query.isFetching}
            className='rounded-none border-t payment-files-table'
            rowSelection={rowSelection}
            expandable={{
              expandedRowKeys:
                typeof state.expandedRow.id === 'string' ? [state.expandedRow.id] : [],
              expandedRowRender(row) {
                return (
                  <ExpandedRow
                    row={row}
                    form={form}
                    isSubmitting={mutation.isLoading}
                    availableLocations={locationsQuery.data ?? []}
                    error={mutation.error ?? undefined}
                    onSave={handleSave}
                    onCancel={handleCancel}
                  />
                )
              },
              expandIcon: ExpandIcon,
              onExpand: handleRowExpand
            }}
            pagination={{
              current: state.pagination.currentPage + 1,
              pageSize: state.pagination.pageSize,
              pageSizeOptions: ['10', '25', '50', '100'],
              showSizeChanger: true,
              total: query.data?.totalFiltered ?? 0,
              showTotal(total, [from, to]) {
                const parts = [
                  `Showing ${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`
                ]

                if (
                  typeof query.data?.totalUnfiltered === 'number' &&
                  query.data.totalUnfiltered !== total
                ) {
                  parts.push(
                    `Total ${query.data.totalUnfiltered} ${query.data.totalUnfiltered === 1 ? 'item' : 'items'}`
                  )
                }

                return <span className='text-sm text-gray-500 font-lato'>{parts.join(' • ')}</span>
              },
              hideOnSinglePage: false
            }}
            onChange={(pagination, filters, sorter, extra) => {
              if (extra.action === 'paginate') {
                dispatch({
                  type: 'set-pagination',
                  payload: {
                    pageSize: pagination.pageSize ?? 10,
                    currentPage: pagination.current ? pagination.current - 1 : 0
                  }
                })
              }

              if (extra.action === 'sort') {
                const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter

                dispatch({
                  type: 'set-sort',
                  payload: {
                    field: singleSorter?.column?.key as ListQuerySortField | undefined,
                    order:
                      typeof singleSorter?.order === 'string'
                        ? ({ ascend: 'asc', descend: 'desc' }[
                            singleSorter.order
                          ] as ListQuerySortOrder)
                        : undefined
                  }
                })
              }

              if (extra.action === 'filter') {
                dispatch({
                  type: 'set-filters',
                  payload: {
                    locationIds: (filters.location ??
                      []) as ListState['searchParams']['locationIds'],
                    fundingTypes: (filters.funding_type ??
                      []) as ListState['searchParams']['fundingTypes'],
                    openAr: (filters.open_ar ?? []) as ListState['searchParams']['openAr'],
                    currentAr: (filters.current_ar ?? []) as ListState['searchParams']['currentAr'],
                    schedule: (filters.schedule_day ?? []) as ListState['searchParams']['schedule'],
                    isActive: (filters.is_active ?? []) as ListState['searchParams']['isActive'],
                    flags: (filters.flags ?? []) as ListState['searchParams']['flags'],
                    pushTypes: (filters.push_type ?? []) as ListState['searchParams']['pushTypes'],
                    networks: (filters.network ?? []) as ListState['searchParams']['networks'],
                    nextPullDate: (filters.next_pull_date ??
                      []) as ListState['searchParams']['nextPullDate']
                  }
                })
              }
            }}
          />
        </div>
      </div>
    </>
  )
}
